/**
 * The trip a browser is part-way through planning, remembered across sessions.
 *
 * The arrangement step saves a trip so that the arrangement has somewhere to live, and every later save in
 * that piece of work — generating the plans, editing the constraints — updates the same trip. What stops a
 * second one being created is remembering the id. That memory lived in the planner's session draft, which is
 * the right home for *unfinished work* and the wrong one for this: measured, leaving the planner and coming
 * back in the same session reused the trip (`75c9ce51…` three times over), and a new session created another
 * (`5acdeed9…`) — so a browser closed and reopened, or a phone app launched again, saved a second trip for the
 * same afternoon's work. The database had twenty-five trips, several of them the same title on the same dates
 * minutes apart.
 *
 * So the id lives here, in `localStorage`, and it is deliberately not the whole draft: what a new session
 * resumes is *the trip*, not the arrangement. The user's places and days are still session work — restoring a
 * week-old selection over a fresh visit would be a screen that argues with the user — but the trip they are
 * adding to is not.
 *
 * Two rules keep it from becoming its own kind of wrong, because "same trip as last time" is exactly what a
 * new trip must not silently be:
 *
 * - **It expires.** A day is long enough to cover closing a laptop and coming back, and short enough that a
 *   trip begun last month does not swallow this afternoon's. An expired anchor is deleted on sight.
 * - **It is dropped once the trip has plans.** A trip with plans is finished work that appears on the trips
 *   list and is opened with the edit route, so the next `/trips/new` is a new trip — which is what its own
 *   name says. Only an *unplanned* trip is one still being made.
 *
 * `now` and the storage are parameters rather than globals so that both rules can be tested without a DOM,
 * and so a browser that refuses storage (private mode, a quota) degrades to "no anchor" instead of throwing.
 */

/** Where the anchor lives. Distinct from the session draft's key, which is a different piece of state. */
export const TRIP_ANCHOR_KEY = "TRIPPOOL_PLANNER_TRIP";

/**
 * How long an anchor is worth trusting.
 *
 * A day: long enough that a user who starts a trip in the evening and finishes the arrangement the next
 * morning continues it, short enough that last month's abandoned trip is not what this afternoon's
 * `/trips/new` quietly edits.
 */
export const TRIP_ANCHOR_TTL_MS = 24 * 60 * 60 * 1000;

type Readable = Pick<Storage, "getItem" | "removeItem">;
type Writable = Pick<Storage, "setItem" | "removeItem">;

/** The stored id, when it is there and still inside its window. Anything else is tidied away and returns null. */
export function readTripAnchor(now: number, storage: Readable | null): string | null {
  if (storage === null) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(TRIP_ANCHOR_KEY);
  } catch {
    // Storage that refuses to be read is storage this feature does not have.
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Written by an older build, or by something else entirely: not an anchor.
    forget(storage);
    return null;
  }

  // Narrowed rather than asserted: the value came out of storage, where anything could have written it, and a
  // cast here would hand the rest of this function a shape nobody checked.
  if (typeof parsed !== "object" || parsed === null) {
    forget(storage);
    return null;
  }

  const id = "id" in parsed ? parsed.id : undefined;
  const at = "at" in parsed ? parsed.at : undefined;
  if (typeof id !== "string" || id === "" || typeof at !== "number" || !Number.isFinite(at)) {
    forget(storage);
    return null;
  }

  if (now - at > TRIP_ANCHOR_TTL_MS) {
    // Yesterday's trip is not what today's new trip is continuing, and leaving the entry behind would make
    // every read repeat the same decision.
    forget(storage);
    return null;
  }

  return id;
}

/** Remember a trip, or forget the one that was remembered when there is none. */
export function writeTripAnchor(id: string | null, now: number, storage: Writable | null): void {
  if (storage === null) return;
  try {
    if (id === null) {
      storage.removeItem(TRIP_ANCHOR_KEY);
      return;
    }
    storage.setItem(TRIP_ANCHOR_KEY, JSON.stringify({ id, at: now }));
  } catch {
    // Ignore private mode or quota errors, exactly as the session draft does.
  }
}

function forget(storage: Pick<Storage, "removeItem">): void {
  try {
    storage.removeItem(TRIP_ANCHOR_KEY);
  } catch {
    // Nothing to do about it, and nothing depends on it having happened.
  }
}
